import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Inbox, BookOpen, Newspaper, Images, Users, Trash2, Plus, ExternalLink,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import logo from "../../assets/logo.png";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [enquiries, setEnquiries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [faculty, setFaculty] = useState([]);

  const load = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(err);
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(err);
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/faculty").then((r) => setFaculty(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const setEnqStatus = async (id, status) => {
    try { await api.patch(`/enquiries/${id}`, { status }); load(); } catch (e) { err(e); }
  };
  const delEnq = async (id) => { try { await api.delete(`/enquiries/${id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };
  const delItem = async (type, id) => { try { await api.delete(`/${type}/${id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };

  const statCards = [
    { label: "Total Enquiries", value: stats.enquiries ?? 0, icon: Inbox },
    { label: "New Enquiries", value: stats.new_enquiries ?? 0, icon: Inbox },
    { label: "Programs", value: stats.courses ?? 0, icon: BookOpen },
    { label: "News Posts", value: stats.news ?? 0, icon: Newspaper },
  ];

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      {/* Topbar */}
      <div className="bg-navy relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-md p-1 ring-1 ring-gold/30">
              <img src={logo} alt="SVS" className="h-11 w-auto object-contain" />
            </div>
            <div>
              <div className="font-serif text-sand text-lg">SVS Admin</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gold">{user?.email}</div>
            </div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sand/80 hover:text-gold transition-colors text-sm">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-navy/10 rounded-[2px] p-6" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
              <s.icon size={22} className="text-gold mb-4" />
              <div className="font-serif text-4xl text-navy">{s.value}</div>
              <div className="text-xs tracking-wide uppercase text-navy/50 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="enquiries">
          <TabsList className="bg-white border border-navy/10 flex-wrap h-auto">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries"><Inbox size={15} className="mr-2" />Enquiries</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses"><BookOpen size={15} className="mr-2" />Programs</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news"><Newspaper size={15} className="mr-2" />News</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery"><Images size={15} className="mr-2" />Gallery</TabsTrigger>
            <TabsTrigger value="faculty" data-testid="tab-faculty"><Users size={15} className="mr-2" />Faculty</TabsTrigger>
          </TabsList>

          {/* Enquiries */}
          <TabsContent value="enquiries" className="mt-6">
            <div className="bg-white border border-navy/10 rounded-[2px] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="enquiries-table">
                  <thead className="bg-sand text-navy/60 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left p-4">Name</th>
                      <th className="text-left p-4">Contact</th>
                      <th className="text-left p-4">Program</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-right p-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-navy/40">No enquiries yet.</td></tr>
                    )}
                    {enquiries.map((e) => (
                      <tr key={e.id} className="border-t border-navy/5" data-testid={`enquiry-row-${e.id}`}>
                        <td className="p-4">
                          <div className="font-medium text-navy">{e.name}</div>
                          {e.message && <div className="text-navy/40 text-xs max-w-xs truncate">{e.message}</div>}
                        </td>
                        <td className="p-4 text-navy/70">
                          <div>{e.email}</div><div className="text-xs">{e.phone}</div>
                        </td>
                        <td className="p-4 text-navy/70">{e.course}</td>
                        <td className="p-4">
                          <Select value={e.status} onValueChange={(v) => setEnqStatus(e.id, v)}>
                            <SelectTrigger className="h-8 w-32 text-xs" data-testid={`enquiry-status-${e.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="enrolled">Enrolled</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4 text-right">
                          <button onClick={() => delEnq(e.id)} className="text-navy/40 hover:text-red-500 transition-colors" data-testid={`delete-enquiry-${e.id}`}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Courses */}
          <TabsContent value="courses" className="mt-6">
            <ManageHeader title="Programs"><CourseDialog onSaved={load} /></ManageHeader>
            <div className="grid md:grid-cols-3 gap-5">
              {courses.map((c) => (
                <div key={c.id} className="bg-white border border-navy/10 rounded-[2px] p-6" data-testid={`admin-course-${c.id}`}>
                  <div className="flex justify-between items-start">
                    <h3 className="font-serif text-lg text-navy">{c.title}</h3>
                    <button onClick={() => delItem("courses", c.id)} className="text-navy/30 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                  <p className="text-xs text-gold mt-1">{c.duration} · {c.seats}</p>
                  <p className="text-navy/50 text-sm mt-3 line-clamp-3">{c.description}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* News */}
          <TabsContent value="news" className="mt-6">
            <ManageHeader title="News & Updates"><NewsDialog onSaved={load} /></ManageHeader>
            <div className="space-y-4">
              {news.map((n) => (
                <div key={n.id} className="bg-white border border-navy/10 rounded-[2px] p-5 flex justify-between items-start" data-testid={`admin-news-${n.id}`}>
                  <div>
                    <div className="flex items-center gap-3 text-xs text-navy/50 mb-1">
                      <span className="text-gold uppercase tracking-wide">{n.category}</span><span>{n.date}</span>
                    </div>
                    <h3 className="font-serif text-lg text-navy">{n.title}</h3>
                    <p className="text-navy/50 text-sm mt-1 max-w-2xl">{n.excerpt}</p>
                  </div>
                  <button onClick={() => delItem("news", n.id)} className="text-navy/30 hover:text-red-500 shrink-0 ml-4"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Gallery */}
          <TabsContent value="gallery" className="mt-6">
            <ManageHeader title="Gallery"><GalleryDialog onSaved={load} /></ManageHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="relative group rounded-[2px] overflow-hidden aspect-square" data-testid={`admin-gallery-${g.id}`}>
                  <img src={g.url} alt={g.caption} className="w-full h-full object-cover" />
                  <button onClick={() => delItem("gallery", g.id)} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Faculty */}
          <TabsContent value="faculty" className="mt-6">
            <ManageHeader title="Faculty"><FacultyDialog onSaved={load} /></ManageHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {faculty.map((f) => (
                <div key={f.id} className="bg-white border border-navy/10 rounded-[2px] p-4 relative group" data-testid={`admin-faculty-${f.id}`}>
                  <img src={f.image} alt={f.name} className="w-full aspect-[3/4] object-cover rounded-[2px] mb-3" />
                  <h3 className="font-serif text-navy">{f.name}</h3>
                  <p className="text-gold text-xs">{f.role}</p>
                  <button onClick={() => delItem("faculty", f.id)} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ManageHeader({ title, children }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <h2 className="font-serif text-2xl text-navy">{title}</h2>
      {children}
    </div>
  );
}

function AddButton({ label = "Add" }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-navy text-sand text-sm font-semibold px-5 py-2.5 hover:bg-navy-light transition-colors cursor-pointer">
      <Plus size={16} className="text-gold" /> {label}
    </span>
  );
}

function CourseDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", short_name: "", duration: "", seats: "", description: "", eligibility: "", highlights: "", order: 0 });
  const save = async () => {
    try {
      await api.post("/courses", { ...f, order: Number(f.order) || 0, highlights: f.highlights.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Program added"); setOpen(false); onSaved();
    } catch (e) { err(e); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="add-course-btn"><AddButton label="Add Program" /></button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif">Add Program</DialogTitle><DialogDescription>Create a new academic program.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Title" data-testid="course-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Short name (e.g. B.Sc)" value={f.short_name} onChange={(e) => setF({ ...f, short_name: e.target.value })} />
            <Input placeholder="Duration (e.g. 4 Years)" value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} />
          </div>
          <Input placeholder="Seats (e.g. 60 Seats)" value={f.seats} onChange={(e) => setF({ ...f, seats: e.target.value })} />
          <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <Input placeholder="Eligibility" value={f.eligibility} onChange={(e) => setF({ ...f, eligibility: e.target.value })} />
          <Input placeholder="Highlights (comma separated)" value={f.highlights} onChange={(e) => setF({ ...f, highlights: e.target.value })} />
        </div>
        <DialogFooter><button onClick={save} data-testid="save-course-btn" className="rounded-full bg-navy text-sand px-6 py-2.5 font-semibold hover:bg-navy-light transition-colors">Save</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewsDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", category: "Announcement", excerpt: "", body: "", date: "" });
  const save = async () => {
    try { await api.post("/news", f); toast.success("News added"); setOpen(false); onSaved(); } catch (e) { err(e); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="add-news-btn"><AddButton label="Add News" /></button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-serif">Add News</DialogTitle><DialogDescription>Publish a new announcement or update.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Title" data-testid="news-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
            <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </div>
          <Textarea placeholder="Excerpt" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} />
          <Textarea placeholder="Full body (optional)" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
        </div>
        <DialogFooter><button onClick={save} data-testid="save-news-btn" className="rounded-full bg-navy text-sand px-6 py-2.5 font-semibold hover:bg-navy-light transition-colors">Save</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GalleryDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ url: "", caption: "", category: "Campus", order: 0 });
  const save = async () => {
    if (!f.url) return toast.error("Image URL required");
    try { await api.post("/gallery", { ...f, order: Number(f.order) || 0 }); toast.success("Image added"); setOpen(false); onSaved(); } catch (e) { err(e); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="add-gallery-btn"><AddButton label="Add Image" /></button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-serif">Add Gallery Image</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Add a public image URL to the gallery.</DialogDescription>
        <div className="space-y-3">
          <Input placeholder="Image URL" data-testid="gallery-url-input" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />
          <Input placeholder="Caption" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} />
          <Input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
          <p className="text-xs text-navy/40 flex items-center gap-1"><ExternalLink size={12} /> Paste a public image URL (e.g. from Unsplash).</p>
        </div>
        <DialogFooter><button onClick={save} data-testid="save-gallery-btn" className="rounded-full bg-navy text-sand px-6 py-2.5 font-semibold hover:bg-navy-light transition-colors">Save</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FacultyDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", role: "", qualification: "", image: "", order: 0 });
  const save = async () => {
    try { await api.post("/faculty", { ...f, order: Number(f.order) || 0 }); toast.success("Faculty added"); setOpen(false); onSaved(); } catch (e) { err(e); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="add-faculty-btn"><AddButton label="Add Faculty" /></button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-serif">Add Faculty</DialogTitle><DialogDescription>Add a faculty member profile.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name" data-testid="faculty-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Input placeholder="Role (e.g. Principal)" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
          <Input placeholder="Qualification" value={f.qualification} onChange={(e) => setF({ ...f, qualification: e.target.value })} />
          <Input placeholder="Photo URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} />
        </div>
        <DialogFooter><button onClick={save} data-testid="save-faculty-btn" className="rounded-full bg-navy text-sand px-6 py-2.5 font-semibold hover:bg-navy-light transition-colors">Save</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
